import { inject, Injectable } from "@angular/core";
import { EspaceClientFacadeService } from "features";
import { OrdoFavoriSeanceService } from "@featuresAY/ordonnance-web-ay/services/ordo-favori-seance.service";
import { UiKitToasterService } from "@jack-sparrow-libraries/ui-kit";
import { LoadingAyWebService } from "@services/loading-ay-web.service";
import { MenuProfileIds } from "@core/layout/components/top-bar/components/menu-profile/enums/menu-profile-ids.enum";
import { MENU_PROFILE_ITEMS } from "@core/layout/components/top-bar/components/menu-profile/constants/menu-profile-items";
import {
	MenuProfileItem,
	MenuProfileSection,
} from "@core/layout/components/top-bar/components/menu-profile/models/menu-profile-section.model";
import { FullClient, FullClientType } from "@models/full-client.model";
import { NavigationService } from "@services/navigation.service";
import { DojoRoutesEnum } from "@core/layout/enums/layout-routes.enum";

const PROFIL_AGATHE_YOU_CARE_LABEL = "Agathe You Care";

@Injectable({
	providedIn: "root",
})
export class MenuProfileService {
	private readonly espaceClientFacadeService = inject(EspaceClientFacadeService);
	private readonly ordoFavoriSeanceService = inject(OrdoFavoriSeanceService);
	private readonly toasterService = inject(UiKitToasterService);
	private readonly loadingService = inject(LoadingAyWebService);
	private readonly navigationService = inject(NavigationService);

	generateItems(fullClient: FullClient, isCare: boolean): MenuProfileSection[] {
		return MENU_PROFILE_ITEMS.map((item) => this.updateItem(item, fullClient, isCare)).filter(
			this.filterItems.bind(this),
		);
	}

	private async openEspaceClient() {
		try {
			await this.loadingService.showSimpleLoading(null, "Nous allons vous rediriger vers votre espace client");
			const url: string | null = (await this.espaceClientFacadeService.getUrl()) || null;
			if (url) {
				window.open(url, "_blank");
			} else {
				throw new Error("No url found");
			}
		} catch (err) {
			console.error(err);
			this.toasterService.show({
				color: "alert",
				titre: "Impossible d'ouvrir l'espace client",
				subtitle: "Une erreur est survenue. Merci de réessayer plus tard.",
			});
			return null;
		} finally {
			await this.loadingService.hide();
		}
	}

	private async openProfilAgatheYou(fullClient: FullClient) {
		if (fullClient && fullClient.type === FullClientType.REMPLACANT) {
			this.navigationService.navDojo([DojoRoutesEnum.MON_COMPTE, DojoRoutesEnum.EDIT_REMPLACANT]);
		} else {
			this.navigationService.navDojo([DojoRoutesEnum.MON_COMPTE, DojoRoutesEnum.EDIT_PS, fullClient.idPs]);
		}
	}

	private updateItem(item: MenuProfileSection, fullClient: FullClient, isCare: boolean): MenuProfileSection {
		if (item.id === MenuProfileIds.ESPACE_CLIENT && "action" in item) {
			return { ...item, action: this.openEspaceClient.bind(this) };
		}
		if ("items" in item) {
			return { ...item, items: item.items.map((subItem) => this.updateSubItem(subItem, fullClient, isCare)) };
		}
		return item;
	}

	private updateSubItem(subItem: MenuProfileItem, fullClient: FullClient, isCare: boolean): MenuProfileItem {
		if (subItem.id === MenuProfileIds.PREFERENCES_MES_SOINS_FAVORIS && "action" in subItem) {
			return {
				...subItem,
				action: this.ordoFavoriSeanceService.openManageFavoriSeancesSideBar.bind(
					this.ordoFavoriSeanceService,
					null,
					true,
				),
			};
		}
		if (subItem.id === MenuProfileIds.PROFIL_AGATHE_YOU && "action" in subItem) {
			return {
				...subItem,
				label: isCare ? PROFIL_AGATHE_YOU_CARE_LABEL : subItem.label,
				action: this.openProfilAgatheYou.bind(this, fullClient),
			};
		}
		return subItem;
	}

	private filterItems(item: MenuProfileSection) {
		if (item.id === MenuProfileIds.ESPACE_CLIENT && "action" in item && !item.action) {
			return false;
		}
		return true;
	}
}
